import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Table from "./Table";
import { getAlertTriagingLeaderboard } from "../services/api";

export default function AlertTriagingLeaderboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "accuracy_sort",
    direction: "desc",
  });

  useEffect(() => {
    setLoading(true);
    getAlertTriagingLeaderboard()
      .then((res) => {
        setData(res.data);
      })
      .finally(() => setLoading(false));
  }, []);

  // Group data by dataset
  const groupedData = data.reduce((acc, row) => {
    const dataset = row.dataset_name;
    if (!acc[dataset]) {
      acc[dataset] = [];
    }
    acc[dataset].push(row);
    return acc;
  }, {});

  // Sort each dataset by accuracy
  Object.keys(groupedData).forEach((dataset) => {
    groupedData[dataset] = groupedData[dataset]
      .sort((a, b) => b.accuracy_sort - a.accuracy_sort)
      .map((row, idx) => ({ ...row, index: idx }));
  });

  const columns = [
    {
      header: "Model",
      accessor: "model_name",
      sortable: true,
      cell: (value, row) => {
        const medals = ["🥇", "🥈", "🥉"];
        const medal = medals[row.index] || "";
        return (
          <span className="font-medium text-gray-900">
            {medal} {value}
          </span>
        );
      },
    },
    {
      header: "True Positives (TP)",
      accessor: "tp",
      sortable: false,
      cell: (value) => <span className="font-mono">{value}</span>,
    },
    {
      header: "False Positives (FP)",
      accessor: "fp",
      sortable: false,
      cell: (value) => <span className="font-mono">{value}</span>,
    },
    {
      header: "Accuracy",
      accessor: "accuracy_sort",
      sortable: true,
      cell: (_, row) => <span className="font-medium">{row.accuracy}</span>,
    },
  ];

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  return (
    <motion.section
      id="alert-triaging-leaderboard"
      className="relative min-h-screen flex items-center justify-center text-gray-800 py-24 overflow-hidden bg-transparent"
      style={{ background: "none" }}
    >
      <div className="relative max-w-5xl mx-auto px-8 z-10">
        <div className="text-center mb-4">
          <div className="flex items-center justify-center gap-2">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-1">
              Alert Triaging Leaderboard
            </h2>
            <button
              onClick={() => setShowInfo(true)}
              className="flex items-center justify-center rounded-full bg-blue-100 hover:bg-blue-200 border border-blue-300 w-7 h-7 ml-2"
              aria-label="How scores are calculated"
              type="button"
            >
              <svg
                className="w-5 h-5 text-blue-700"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth={2}
                  fill="white"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 16v-4m0-4h.01"
                />
              </svg>
            </button>
          </div>
          <p className="text-md text-gray-600 max-w-3xl mx-auto">
            Performance comparison of different state-of-the-art (SOTA) models
            on alert triaging tasks.
          </p>
        </div>

        {showInfo && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/40">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-xl shadow-lg p-8 w-full max-w-lg relative"
            >
              <button
                className="absolute top-2 right-4 text-gray-400 hover:text-gray-700 text-2xl"
                onClick={() => setShowInfo(false)}
                aria-label="Close"
              >
                ×
              </button>
              <h3 className="text-lg font-bold mb-2">
                How Scores Are Calculated
              </h3>
              <div className="text-sm text-gray-700">
                <ul className="mb-3 list-disc pl-6">
                  <li>
                    <span className="font-semibold">True Positives (TP)</span>:
                    <br />
                    <code>
                      Number of correctly identified positive alerts out of
                      total positive alerts.
                    </code>
                  </li>
                  <li>
                    <span className="font-semibold">False Positives (FP)</span>
                    :
                    <br />
                    <code>
                      Number of correctly identified negative alerts out of
                      total negative alerts.
                    </code>
                  </li>
                  <li>
                    <span className="font-semibold">Accuracy</span>:
                    <br />
                    <code>Average of TP and FP percentages.</code>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        )}

        {/* TH-SRC-23 Dataset Table */}
        {groupedData["TH-SRC-23"] && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              TH-SRC-23 Dataset (50 TP and 50 FP)
            </h3>
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <Table
                columns={columns}
                data={groupedData["TH-SRC-23"]}
                isLoading={loading}
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>
          </div>
        )}

        {/* CIC-IDS2017 Dataset Table */}
        {groupedData["CIC-IDS2017"] && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              CIC-IDS2017 Dataset (5 TP and 30 FP)
            </h3>
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <Table
                columns={columns}
                data={groupedData["CIC-IDS2017"]}
                isLoading={loading}
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </div>
          </div>
        )}

        {/* Info footer */}
        <div className="bg-gray-50 rounded-lg px-4 py-2 text-xs text-gray-500 flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 text-blue-500 mr-1.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>
            Results calculated from the alert triaging benchmark dataset.
          </span>
        </div>
      </div>
    </motion.section>
  );
}